import React, { MutableRefObject, ReactNode, useCallback } from 'react';

import { isNil } from 'lodash-es';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ServiceMode } from '@rbi-ctg/menu';
import Dropdown from 'components/app-header/dropdown';
import { LeftNav } from 'components/app-header/left-nav';
import { RightNav } from 'components/app-header/right-nav';
import styleLoadingIndicator from 'components/app-header/style-loading-indicator';
import { AppSubheader } from 'components/app-subheader';
import CartPreview from 'components/cart-preview';
import { DeliveryBannerContainer } from 'components/delivery-banner';
import Link from 'components/link';
import Logo from 'components/logo';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoadingIndicator } from 'state/loading';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useStoreContext } from 'state/store';
import { routes } from 'utils/routing';

import NavContainer from '../styled/nav-container';

import LogoContainer from './logo-container';

const DesktopHeaderContainer = styled.div`
  display: none;

  ${Styles.desktop`
    display: block;
    position: fixed;
    transform: translateY(0) scale(1);
    transition: transform 0.3s ease-out;
    width: 100%;
    will-change: transform;
    z-index: ${Styles.zIndex.overlay + 100};
  `}
`;

const MeasuredContainer = styled.div<{
  children: ReactNode;
  ref: HTMLDivElement;
}>`
  width: 100%;
  height: 100%;
`;

// This causes "extra" nav elements to be hidden (per product req), as opposed to
// wrapping and looking broken on small desktop screens when there are > 3 nav elements.
const LeftNavWrapper = styled.div`
  max-height: 35px;
  max-width: 40%;
  overflow: hidden;
  padding: 5px;
`;

const RightNavContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
`;

const FlexContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: space-between;
  position: relative;
`;

const NoUnderlineLink = styled(Link)`
  position: static;
  color: ${Styles.color.white};

  &:hover {
    color: ${Styles.color.white};
  }

  ::before {
    color: transparent;
  }
`;

const Center = styled.div`
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
  z-index: ${Styles.zIndex.max - 300};
`;

interface IDesktopHeaderProps {
  serviceMode: ServiceMode;
  userAddress: string;
  cateringPickupDateTime: string;
}

const DesktopHeader: React.FC<IDesktopHeaderProps> = React.forwardRef(
  ({ serviceMode, userAddress, cateringPickupDateTime }, ref: MutableRefObject<HTMLDivElement>) => {
    const { formatMessage } = useIntl();
    const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
    const enableBottomService = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
    const mParticle = useMParticleContext();
    const { isStoreOpenAndAvailable } = useStoreContext();
    const LoadingIndicator = useLoadingIndicator();
    const StyledLoadingIndicator = styleLoadingIndicator(LoadingIndicator);
    const { featureAboutMenuEntriesDesktop: aboutMenuEntries } = useFeatureNavigation();

    // onReFocus Sets the focus of the clicked element to itself
    //
    // This is necessary due to the way that Reach Router changes the focus.
    // After the initial page load, reach router will move the focus to the outer most
    // component that is wrapped by the router. This component is outside of router,
    // so when clicking in the nav the focus is being pulled into the page.
    // For accessibility reasons, this is not the desired behavior.
    const onReFocus = useCallback(e => {
      e.persist();
      let target = e.target;
      window.setTimeout(() => {
        if (target && target.focus) {
          target.focus();
        }
      }, 200);
    }, []);

    const onLogoClick = useCallback(
      e => {
        mParticle.logNavigationClick(CustomEventNames.BUTTON_CLICK_LOGO);
        onReFocus(e);
      },
      [mParticle, onReFocus]
    );

    const onMyAccountClick = useCallback(
      e => {
        mParticle.logNavigationClick(CustomEventNames.BUTTON_CLICK_MY_ACCOUNT);
        onReFocus(e);
      },
      [mParticle, onReFocus]
    );

    const enableCart = isNil(enableOrdering) || enableOrdering;

    return (
      <DesktopHeaderContainer>
        <MeasuredContainer ref={ref as any}>
          <NavContainer aria-label={formatMessage(messages.primaryNavLinks)}>
            <FlexContainer>
              <LeftNavWrapper>
                <LeftNav />
                {aboutMenuEntries && (
                  <Dropdown
                    title={aboutMenuEntries?.name?.locale ?? ''}
                    entries={aboutMenuEntries?.pages ?? []}
                  />
                )}
              </LeftNavWrapper>

              <Center>
                <NoUnderlineLink to={routes.base} onClick={onLogoClick}>
                  <LogoContainer>
                    <Logo aria-hidden="true" />
                  </LogoContainer>
                </NoUnderlineLink>
              </Center>

              <RightNavContainer>
                <RightNav onMyAccountClick={onMyAccountClick} onReFocus={onReFocus} />
                {enableCart && <CartPreview isVisible={false} />}
              </RightNavContainer>
            </FlexContainer>
          </NavContainer>
          <StyledLoadingIndicator
            hasStoreSelection={isStoreOpenAndAvailable && !enableBottomService}
          />
          <DeliveryBannerContainer />
          <AppSubheader
            serviceMode={serviceMode}
            userAddress={userAddress}
            cateringPickupDateTime={cateringPickupDateTime}
          />
        </MeasuredContainer>
      </DesktopHeaderContainer>
    );
  }
);

export default DesktopHeader;
const messages = defineMessages({
  primaryNavLinks: {
    id: 'primaryNavLinks',
    defaultMessage: 'Primary Navigation',
    description: 'Aria label letting the user know that this is the navigation'
  }
});
